import { Component, DestroyRef, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NotesService } from '@core/service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { FluidModule } from 'primeng/fluid';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-wound-location-dialog',
    templateUrl: './location-note-dialog.component.html',
    styleUrls: ['./location-note-dialog.component.scss'],
    imports: [
        FluidModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ReactiveFormsModule
]
})
export class LocationNoteDialogComponent implements OnInit {
  indexOfDoc: number;
  memberId: string;
  createdBy: string;
  tenantId: string;
  item: any;
  formModel: FormGroup = new FormGroup({
    locationNote: new FormControl(null, [Validators.required, Validators.maxLength(250), this.uniqueName()])
  });
  showModal: boolean;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private notesService: NotesService,
    private readonly destroyRef: DestroyRef,
  ) { }

  ngOnInit() {
    this.indexOfDoc = this.config.data.indexOfDoc;
    this.memberId = this.config.data.memberId;
    this.createdBy = this.config.data.createdBy;
    this.tenantId = this.config.data.tenantId;
    this.item = this.config.data.item;
    this.showModal = true;

    // if we're editing a wound location
    if (this.item) {
      this.formModel.patchValue({
        locationNote: this.item.location
      });
    }
  }

  uniqueName(): ValidatorFn {
    return (group: FormGroup): { [key: string]: any } => {

      // nothing to check if it's empty
      if (!group.value) {
        return null;
      }

      const locationNotes = this.config.data.locationNotes as any[];

      // if we're editing an item
      if (this.item) {

        // get all wound locations that don't include the passed in (editable) item
        const wlocs = locationNotes.filter(wl => wl.location != this.item.location);

        // check if the location matches any of them
        const alreadyExists = wlocs.filter(wl => wl.location == group.value).length > 0;

        // if so not valid
        if (alreadyExists) {
          return { 'formValid': false };
        }
      }
      // adding a new location
      else {
        // check if the location matches any of the wound locations
        const alreadyExists = locationNotes.filter(wl => wl.location == group.value).length > 0;

        // if so not valid
        if (alreadyExists) {
          return { 'formValid': false };
        }
      }

      // otherwise valid
      return null;
    }
  }

  close() {
    this.ref.close({ indexOfDoc: this.indexOfDoc });
    this.showModal = false;
  }

  onSubmit() {
    const model = this.getModel();

    if (this.item) {
      this.notesService.editLocationNote(model)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(result => {
          // make sure to pass along canEdit for before saving edits (after saving can't edit)
          this.ref.close({ result: { ...result, canEdit: model.canEdit }, indexOfDoc: this.indexOfDoc });
          this.showModal = false;
        });
    }
    else {
      this.notesService.createLocationNote(model)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(result => {
          // can still modify newly added locations before saving note
          this.ref.close({ result: { ...result, canEdit: true }, indexOfDoc: this.indexOfDoc });
          this.showModal = false;
        });
    }
  }

  getModel(): any {
    return {
      ...this.item,
      indexOfDoc: this.indexOfDoc,
      location: this.formModel.controls['locationNote'].value,
      memberId: this.memberId,
      createdBy: this.createdBy
    };
  }
}
